import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency } from "@/utils/format";
import { CustoMensalStat } from "../chartUtils";
import { BAR_HOVER_PROPS, lineDots, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

/* ─── Tooltip customizado ────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[220px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">{formatCurrency(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:        CustoMensalStat[];
  loading:     boolean;
  unitCount:   number;
  ccName?:     string;
}

export function CustoAcumuladoChart({ data, loading, unitCount, ccName }: Props) {
  const subtitle = ccName
    ? `Evolução mensal e acumulado — ${ccName}`
    : "Evolução mensal e acumulado de custos da obra";

  const showUnitCost = unitCount > 0;

  return (
    <ChartContainer
      title="Evolução do Custo Acumulado"
      subtitle={subtitle}
      loading={loading}
      empty={!loading && data.length === 0}
      height={300}
    >
      <ComposedChart data={data} margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="mes"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={60}
          tickFormatter={(v: number) =>
            v >= 1_000_000
              ? `${(v / 1_000_000).toFixed(1)}M`
              : `${(v / 1_000).toFixed(0)}K`
          }
        />
        {showUnitCost && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={60}
            tickFormatter={(v: number) =>
              v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(1)}M`
                : `${(v / 1_000).toFixed(0)}K`
            }
          />
        )}
        <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />

        {/* Barras de custo mensal */}
        <Bar
          {...BAR_HOVER_PROPS}
          yAxisId="left"
          dataKey="custo"
          name="Custo Mensal (R$)"
          fill="#6366f1"
          radius={[4, 4, 0, 0]}
          maxBarSize={40}
          opacity={0.75}
        />

        {/* Linha de custo acumulado */}
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="custoAcum"
          name="Custo Acumulado (R$)"
          stroke="#e74c3c"
          strokeWidth={2.5}
          {...lineDots("#e74c3c")}
        />

        {/* Linha de custo unitário acumulado (só quando há casas) */}
        {showUnitCost && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="custoUnitAcum"
            name="Custo Unitário Acum. (R$/casa)"
            stroke="#f59e0b"
            strokeWidth={2}
            strokeDasharray="5 3"
            {...lineDots("#f59e0b")}
          />
        )}

        {data.length > 0 && (
          <ReferenceLine
            yAxisId="left"
            y={data[data.length - 1]?.custoAcum}
            stroke="#e74c3c"
            strokeDasharray="4 4"
            strokeOpacity={0.3}
          />
        )}
      </ComposedChart>
    </ChartContainer>
  );
}
